import { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  ExternalLink,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
  UserRound,
  XCircle,
} from "lucide-react";

import {
  Assignment,
  AssignmentSubmission,
  Cohort,
  programApi,
} from "../api/program";
import MaterialDropzone from "../components/NewView/Common/MaterialDropzone";
import ProgramMaterials from "../components/NewView/Common/ProgramMaterials";

const initialForm = {
  title: "",
  description: "",
  weekNumber: 1,
  type: "text" as Assignment["type"],
  publishAt: "",
  dueAt: "",
  materialUrl: "",
  points: 30,
};

const DATETIME_STEP_SECONDS = 5 * 60;

const getDefaultDateTimeInput = (hoursToAdd = 0) => {
  const date = new Date();
  date.setMinutes(0, 0, 0);
  date.setHours(date.getHours() + hoursToAdd);

  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const AdminAssignmentsPage = ({
  embedded = false,
  createSignal = 0,
  onAssignmentCreated,
}: {
  embedded?: boolean;
  createSignal?: number;
  onAssignmentCreated?: () => void | Promise<void>;
}) => {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [cohortId, setCohortId] = useState("");
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [form, setForm] = useState(initialForm);
  const [selected, setSelected] = useState<Assignment | null>(null);
  const [participants, setParticipants] = useState<
    Array<{
      user: {
        _id: string;
        username: string;
        telephone: string;
        email?: string;
      };
      submission: AssignmentSubmission | null;
    }>
  >([]);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [awardedPoints, setAwardedPoints] = useState<Record<string, number>>({});
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState("");
  const reviewSectionRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [createMaterialFiles, setCreateMaterialFiles] = useState<File[]>([]);
  const [selectedMaterialFiles, setSelectedMaterialFiles] = useState<File[]>([]);
  const [materialsSaving, setMaterialsSaving] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const loadAssignments = useCallback(async (selectedCohort: string) => {
    try {
      setAssignments(
        selectedCohort
          ? await programApi.listAdminAssignments(selectedCohort)
          : []
      );
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося завантажити завдання."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadCohorts = async () => {
      try {
        const data = await programApi.listCohorts();
        setCohorts(data);
        setCohortId(data[0]?._id || "");
      } catch (error: any) {
        setMessage(
          error.response?.data?.message || "Не вдалося завантажити потоки."
        );
        setLoading(false);
      }
    };
    loadCohorts();
  }, []);

  useEffect(() => {
    if (cohortId) loadAssignments(cohortId);
  }, [cohortId, loadAssignments]);

  useEffect(() => {
    if (embedded && createSignal > 0) {
      setCreateModalOpen(true);
    }
  }, [createSignal, embedded]);

  const create = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setSaving(true);
      const assignment = await programApi.createAssignment({
        cohortId,
        ...form,
        publishAt: new Date(form.publishAt).toISOString(),
        dueAt: new Date(form.dueAt).toISOString(),
        status: "published",
      });
      if (createMaterialFiles.length) {
        await programApi.addAssignmentMaterials(assignment._id, {
          files: createMaterialFiles,
        });
      }
      setForm(initialForm);
      setCreateMaterialFiles([]);
      setCreateModalOpen(false);
      setMessage("Завдання створено і додано до календаря.");
      await loadAssignments(cohortId);
      await onAssignmentCreated?.();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося створити завдання."
      );
    } finally {
      setSaving(false);
    }
  };

  const openReview = async (assignment: Assignment) => {
    try {
      setReviewLoading(true);
      setSelected(assignment);
      setParticipants([]);
      window.setTimeout(() => {
        reviewSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);
      const data = await programApi.getAssignmentSubmissions(assignment._id);
      setSelected(data.assignment);
      setParticipants(data.participants);
      setComments(
        Object.fromEntries(
          data.participants
            .filter(({ submission }) => submission)
            .map(({ submission }) => [
              submission!._id,
              submission!.adminComment || "",
            ])
        )
      );
      setAwardedPoints(
        Object.fromEntries(
          data.participants
            .filter(({ submission }) => submission)
            .map(({ submission }) => [
              submission!._id,
              submission!.awardedPoints || assignment.points,
            ])
        )
      );
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося отримати відповіді."
      );
    } finally {
      setReviewLoading(false);
    }
  };

  const review = async (
    submission: AssignmentSubmission,
    status: "approved" | "revision" | "rejected"
  ) => {
    if (!selected) return;
    try {
      setReviewingId(submission._id);
      await programApi.reviewSubmission(submission._id, {
        status,
        adminComment: comments[submission._id] || "",
        awardedPoints:
          status === "approved"
            ? Math.min(
                selected.points,
                Math.max(0, awardedPoints[submission._id] ?? selected.points)
              )
            : 0,
      });
      setMessage("Результат перевірки збережено.");
      await openReview(selected);
      await loadAssignments(cohortId);
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося зберегти перевірку."
      );
    } finally {
      setReviewingId("");
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Видалити завдання та всі відповіді?")) return;
    await programApi.deleteAssignment(id);
    if (selected?._id === id) {
      setSelected(null);
      setParticipants([]);
    }
    await loadAssignments(cohortId);
  };

  const closeCreateModal = () => {
    if (saving) return;
    setCreateModalOpen(false);
    setForm(initialForm);
    setCreateMaterialFiles([]);
  };

  const addSelectedMaterials = async () => {
    if (!selected) return;

    if (!selectedMaterialFiles.length) {
      setMessage("Додай файл матеріалу.");
      return;
    }

    try {
      setMaterialsSaving(true);
      const assignment = await programApi.addAssignmentMaterials(selected._id, {
        files: selectedMaterialFiles,
      });
      setSelected(assignment);
      setSelectedMaterialFiles([]);
      setMessage("Матеріали додано.");
      await loadAssignments(cohortId);
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося додати матеріали."
      );
    } finally {
      setMaterialsSaving(false);
    }
  };

  const deleteSelectedMaterial = async (materialId: string) => {
    if (!selected) return;

    try {
      setMaterialsSaving(true);
      const assignment = await programApi.deleteAssignmentMaterial(
        selected._id,
        materialId
      );
      setSelected(assignment);
      setMessage("Матеріал видалено.");
      await loadAssignments(cohortId);
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося видалити матеріал."
      );
    } finally {
      setMaterialsSaving(false);
    }
  };

  return (
    <section
      className={
        embedded
          ? "min-h-full"
          : "min-h-full bg-[#F5F3EF] px-4 py-7 sm:px-6 lg:px-8"
      }
    >
      <div className="mx-auto max-w-[1450px]">
        {!embedded && (
        <div className="mb-6 flex items-end justify-between">
          <div>
            <p className="text-sm font-semibold text-[#B1542E]">
              Кабінет адміністратора
            </p>
            <h2 className="mt-1 text-3xl font-extrabold text-[#29251F]">
              Завдання по тижнях
            </h2>
          </div>
          <button
            onClick={() => loadAssignments(cohortId)}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-[#DDD7CF] bg-white px-4 text-sm font-semibold"
          >
            <RefreshCw size={16} /> Оновити
          </button>
        </div>
        )}

        {message && (
          <div className="mb-5 rounded-xl bg-[#FFF8F3] px-4 py-3 text-sm text-[#8A4A2E]">
            {message}
          </div>
        )}

        {createModalOpen && (
          <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/45 px-3 py-4 backdrop-blur-sm sm:items-center">
            <button
              type="button"
              onClick={closeCreateModal}
              className="absolute inset-0"
              aria-label="Закрити форму"
            />
            <form
              onSubmit={create}
              className="relative z-10 flex max-h-[92dvh] w-full max-w-2xl flex-col overflow-hidden rounded-[26px] border-2 border-black bg-white shadow-[8px_8px_0_#ffd400]"
            >
              <div className="flex shrink-0 items-start justify-between gap-3 border-b-2 border-black bg-[#fffdf2] px-4 py-5 sm:px-5">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]">
                    <ClipboardCheck size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xl font-black tracking-[-0.03em] text-black">
                      Нове завдання
                    </h3>
                    <p className="text-sm leading-5 text-[#857E76]">
                      Завдання з'явиться у кабінеті учасників вибраного потоку.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border-2 border-black bg-white text-black transition hover:bg-[#ffd400]"
                  aria-label="Закрити"
                >
                  <XCircle size={18} />
                </button>
              </div>

              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
                <label className="block">
                  <span className="text-sm font-black text-[#312D28]">
                    Потік
                  </span>
                  <select
                    required
                    value={cohortId}
                    onChange={(event) => setCohortId(event.target.value)}
                    className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3"
                  >
                    <option value="">Оберіть потік</option>
                    {cohorts.map((cohort) => (
                      <option key={cohort._id} value={cohort._id}>
                        {cohort.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="text-sm font-black text-[#312D28]">
                    Назва завдання
                  </span>
                  <input
                    required
                    value={form.title}
                    onChange={(event) =>
                      setForm({ ...form, title: event.target.value })
                    }
                    className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-black text-[#312D28]">
                    Опис і умови виконання
                  </span>
                  <textarea
                    required
                    rows={5}
                    value={form.description}
                    onChange={(event) =>
                      setForm({ ...form, description: event.target.value })
                    }
                    className="mt-2 w-full rounded-xl border border-[#DCD7CF] px-3 py-2"
                  />
                </label>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label>
                    <span className="text-sm font-black text-[#312D28]">
                      Тиждень програми
                    </span>
                    <input
                      type="number"
                      min={1}
                      value={form.weekNumber}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          weekNumber: Number(event.target.value),
                        })
                      }
                      className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3"
                    />
                  </label>
                  <label>
                    <span className="text-sm font-black text-[#312D28]">
                      Тип відповіді
                    </span>
                    <select
                      value={form.type}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          type: event.target.value as Assignment["type"],
                        })
                      }
                      className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3"
                    >
                      <option value="text">Текст</option>
                      <option value="link">Посилання</option>
                      <option value="file">Файл за посиланням</option>
                      <option value="test">Тест</option>
                    </select>
                  </label>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label>
                    <span className="text-sm font-black text-[#312D28]">
                      Публікація
                    </span>
                    <input
                      required
                      type="datetime-local"
                      step={DATETIME_STEP_SECONDS}
                      value={form.publishAt}
                      onFocus={() => {
                        if (!form.publishAt) {
                          setForm({
                            ...form,
                            publishAt: getDefaultDateTimeInput(),
                          });
                        }
                      }}
                      onChange={(event) =>
                        setForm({ ...form, publishAt: event.target.value })
                      }
                      className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-2"
                    />
                  </label>
                  <label>
                    <span className="text-sm font-black text-[#312D28]">
                      Дедлайн
                    </span>
                    <input
                      required
                      type="datetime-local"
                      step={DATETIME_STEP_SECONDS}
                      value={form.dueAt}
                      onFocus={() => {
                        if (!form.dueAt) {
                          setForm({
                            ...form,
                            dueAt: getDefaultDateTimeInput(1),
                          });
                        }
                      }}
                      onChange={(event) =>
                        setForm({ ...form, dueAt: event.target.value })
                      }
                      className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-2"
                    />
                  </label>
                </div>

                <div className="rounded-2xl border border-[#E3DED6] bg-[#fffdf7] p-4">
                  <p className="text-sm font-black text-[#312D28]">
                    Прикріплені матеріали
                  </p>
                  <p className="mt-1 text-xs font-semibold text-[#817A72]">
                    Додай файли, які побачить учасник.
                  </p>
                  <div className="mt-3">
                    <MaterialDropzone
                      files={createMaterialFiles}
                      onFilesChange={setCreateMaterialFiles}
                    />
                  </div>
                </div>

                <label className="block">
                  <span className="text-sm font-black text-[#312D28]">
                    Максимальна кількість балів
                  </span>
                  <input
                    type="number"
                    min={0}
                    value={form.points}
                    onChange={(event) =>
                      setForm({ ...form, points: Number(event.target.value) })
                    }
                    className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3"
                  />
                </label>
              </div>

              <div className="shrink-0 border-t border-[#EEEAE4] bg-white px-4 py-4 sm:px-5">
                <button
                  disabled={saving}
                  className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000] disabled:opacity-60"
                >
                  <ClipboardCheck size={17} />
                  {saving ? "Створюємо..." : "Створити завдання"}
                </button>
              </div>
            </form>
          </div>
        )}

        <div
          className={
            embedded
              ? "grid min-w-0 gap-6"
              : "grid min-w-0 gap-6 xl:grid-cols-[380px_minmax(0,1fr)]"
          }
        >
          {!embedded && (
          <form
            onSubmit={create}
            className="h-fit rounded-2xl border border-[#E3DED6] bg-white"
          >
            <div className="border-b border-[#EEEAE4] p-5">
              <h3 className="flex items-center gap-2 font-bold text-[#312D28]">
                <Plus size={18} className="text-[#F16126]" />
                Нове завдання
              </h3>
            </div>
            <div className="space-y-4 p-4 sm:p-5">
              <label className="block">
                <span className="text-sm font-black text-[#312D28]">
                  Потік
                </span>
                <span className="mt-1 block text-xs font-semibold text-[#817A72]">
                  Обери групу учасників, які отримають це завдання.
                </span>
                <select
                  required
                  value={cohortId}
                  onChange={(event) => setCohortId(event.target.value)}
                  className="mt-2 h-11 w-full rounded-lg border border-[#DCD7CF] px-3"
                >
                  <option value="">Оберіть потік</option>
                  {cohorts.map((cohort) => (
                    <option key={cohort._id} value={cohort._id}>
                      {cohort.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-sm font-black text-[#312D28]">
                  Назва завдання
                </span>
                <span className="mt-1 block text-xs font-semibold text-[#817A72]">
                  Коротка назва, яку бачитиме учасник у кабінеті.
                </span>
                <input
                  required
                  placeholder="Наприклад: Підготувати резюме"
                  value={form.title}
                  onChange={(event) =>
                    setForm({ ...form, title: event.target.value })
                  }
                  className="mt-2 h-11 w-full rounded-lg border border-[#DCD7CF] px-3"
                />
              </label>

              <label className="block">
                <span className="text-sm font-black text-[#312D28]">
                  Опис і умови виконання
                </span>
                <span className="mt-1 block text-xs font-semibold text-[#817A72]">
                  Поясни, що саме потрібно зробити та як здати відповідь.
                </span>
                <textarea
                  required
                  rows={5}
                  placeholder="Опиши завдання, критерії та формат відповіді"
                  value={form.description}
                  onChange={(event) =>
                    setForm({ ...form, description: event.target.value })
                  }
                  className="mt-2 w-full rounded-lg border border-[#DCD7CF] px-3 py-2"
                />
              </label>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-black text-[#312D28]">
                    Тиждень програми
                  </span>
                  <span className="mt-1 block text-xs font-semibold text-[#817A72]">
                    До якого тижня віднести завдання.
                  </span>
                  <input
                    type="number"
                    min={1}
                    value={form.weekNumber}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        weekNumber: Number(event.target.value),
                      })
                    }
                    className="mt-2 h-11 w-full rounded-lg border border-[#DCD7CF] px-3"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-black text-[#312D28]">
                    Тип відповіді
                  </span>
                  <span className="mt-1 block text-xs font-semibold text-[#817A72]">
                    Що має надіслати учасник.
                  </span>
                  <select
                    value={form.type}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        type: event.target.value as Assignment["type"],
                      })
                    }
                    className="mt-2 h-11 w-full rounded-lg border border-[#DCD7CF] px-3"
                  >
                    <option value="text">Текст</option>
                    <option value="link">Посилання</option>
                    <option value="file">Файл за посиланням</option>
                    <option value="test">Тест</option>
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="text-sm font-black text-[#312D28]">
                  Дата і час публікації
                </span>
                <span className="mt-1 block text-xs font-semibold text-[#817A72]">
                  Коли завдання стане доступним учасникам.
                </span>
                <input
                  required
                  type="datetime-local"
                  step={DATETIME_STEP_SECONDS}
                  value={form.publishAt}
                  onFocus={() => {
                    if (!form.publishAt) {
                      setForm({
                        ...form,
                        publishAt: getDefaultDateTimeInput(),
                      });
                    }
                  }}
                  onChange={(event) =>
                    setForm({ ...form, publishAt: event.target.value })
                  }
                  className="mt-2 h-11 w-full rounded-lg border border-[#DCD7CF] px-2"
                />
              </label>

              <label className="block">
                <span className="text-sm font-black text-[#312D28]">
                  Дедлайн здачі
                </span>
                <span className="mt-1 block text-xs font-semibold text-[#817A72]">
                  Крайній термін, до якого учасник має здати роботу.
                </span>
                <input
                  required
                  type="datetime-local"
                  step={DATETIME_STEP_SECONDS}
                  value={form.dueAt}
                  onFocus={() => {
                    if (!form.dueAt) {
                      setForm({
                        ...form,
                        dueAt: getDefaultDateTimeInput(1),
                      });
                    }
                  }}
                  onChange={(event) =>
                    setForm({ ...form, dueAt: event.target.value })
                  }
                  className="mt-2 h-11 w-full rounded-lg border border-[#DCD7CF] px-2"
                />
              </label>

              <div className="rounded-2xl border border-[#E3DED6] bg-[#fffdf7] p-4">
                <p className="text-sm font-black text-[#312D28]">
                  Прикріплені матеріали
                </p>
                <p className="mt-1 text-xs font-semibold text-[#817A72]">
                  Додай файли, які побачить учасник.
                </p>
                <div className="mt-3">
                  <MaterialDropzone
                    files={createMaterialFiles}
                    onFilesChange={setCreateMaterialFiles}
                  />
                </div>
              </div>

              <label className="block">
                <span className="text-sm font-black text-[#312D28]">
                  Максимальна кількість балів
                </span>
                <span className="mt-1 block text-xs font-semibold text-[#817A72]">
                  Скільки балів можна отримати за це завдання.
                </span>
                <input
                  type="number"
                  min={0}
                  value={form.points}
                  onChange={(event) =>
                    setForm({ ...form, points: Number(event.target.value) })
                  }
                  className="mt-2 h-11 w-full rounded-lg border border-[#DCD7CF] px-3"
                />
              </label>

              <button
                disabled={saving}
                className="h-11 w-full rounded-lg bg-[#F16126] font-bold text-white disabled:opacity-60"
              >
                {saving ? "Створюємо..." : "Створити завдання"}
              </button>
            </div>
          </form>
          )}

          <div className="flex min-w-0 flex-col gap-5">
            {loading ? (
              <div className="rounded-2xl bg-white p-8">Завантажуємо...</div>
            ) : (
              <div className="order-2 grid gap-3 lg:grid-cols-2">
                {assignments.map((assignment) => (
                  <article
                    key={assignment._id}
                    className="min-w-0 rounded-2xl border border-[#E3DED6] bg-white p-4 sm:p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <span className="text-xs font-bold text-[#7657B5]">
                          Тиждень {assignment.weekNumber}
                        </span>
                        <h3 className="mt-2 break-words text-lg font-bold text-[#312D28]">
                          {assignment.title}
                        </h3>
                      </div>
                      <button
                        onClick={() => remove(assignment._id)}
                        className="text-[#9D3D2E]"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                    <p className="mt-3 text-sm text-[#777068]">
                      Дедлайн:{" "}
                      {new Date(assignment.dueAt).toLocaleString("uk-UA")}
                    </p>
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs font-semibold text-[#817A72]">
                        На перевірці:{" "}
                        {assignment.submissionStats?.submitted || 0}
                      </span>
                      <button
                        onClick={() => openReview(assignment)}
                        className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-[#312D28] px-3 text-xs font-bold text-white sm:w-auto"
                      >
                        <ClipboardCheck size={15} />
                        {assignment.submissionStats?.submitted
                          ? `Переглянути ${assignment.submissionStats.submitted}`
                          : "Переглянути роботи"}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {selected && (
              <div
                ref={reviewSectionRef}
                className="order-1 scroll-mt-24 overflow-hidden rounded-2xl border border-[#D9CEF0] bg-white shadow-sm"
              >
                <div className="border-b border-[#EEEAE4] p-4 sm:p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-[#7657B5]">
                        Перевірка робіт
                      </p>
                      <h3 className="mt-1 break-words text-xl font-bold text-[#312D28]">
                        {selected.title}
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs font-semibold">
                      <span className="rounded-full bg-[#FFF0E8] px-3 py-1.5 text-[#C74712]">
                        Очікують:{" "}
                        {
                          participants.filter(
                            ({ submission }) =>
                              submission?.status === "submitted"
                          ).length
                        }
                      </span>
                      <span className="rounded-full bg-[#EEF8F3] px-3 py-1.5 text-[#25835C]">
                        Зараховано:{" "}
                        {
                          participants.filter(
                            ({ submission }) =>
                              submission?.status === "approved"
                          ).length
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  <div className="mb-5 rounded-2xl border border-[#E3DED6] bg-[#fffdf7] p-4">
                    <ProgramMaterials
                      materials={selected.materials}
                      title="Прикріплені файли завдання"
                      emptyText="До цього завдання ще не прикріплено жодного файлу."
                      onDelete={deleteSelectedMaterial}
                    />
                    <div className="mt-3">
                      <p className="text-sm font-black text-[#312D28]">
                        Додати матеріали
                      </p>
                      <p className="mt-1 text-xs font-semibold text-[#817A72]">
                        До 5 файлів за раз, максимум 25 MB кожен.
                      </p>
                    </div>
                    <div className="mt-3">
                      <MaterialDropzone
                        files={selectedMaterialFiles}
                        onFilesChange={setSelectedMaterialFiles}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={addSelectedMaterials}
                      disabled={materialsSaving}
                      className="mt-3 inline-flex h-10 w-full items-center justify-center rounded-xl border-2 border-black bg-white px-4 text-xs font-black text-black shadow-[3px_3px_0_#ffd400] disabled:opacity-60 sm:w-auto"
                    >
                      {materialsSaving ? "Зберігаємо..." : "Додати матеріали"}
                    </button>
                  </div>

                  {reviewLoading ? (
                    <div className="flex items-center justify-center gap-2 py-10 text-sm text-[#777068]">
                      <RefreshCw size={17} className="animate-spin" />
                      Завантажуємо роботи учасників...
                    </div>
                  ) : participants.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[#DCD7CF] bg-[#FAF9F7] px-4 py-10 text-center">
                      <UserRound
                        size={28}
                        className="mx-auto text-[#A39B93]"
                      />
                      <p className="mt-3 font-bold text-[#4A443E]">
                        У потоці немає учасників
                      </p>
                      <p className="mt-1 text-sm text-[#817A72]">
                        Перевірте, чи користувач доданий до цього потоку.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {participants.map(({ user, submission }) => (
                        <article
                          key={user._id}
                          className={`rounded-xl border p-4 ${
                            submission?.status === "submitted"
                              ? "border-[#D9CEF0] bg-[#FCFAFF]"
                              : "border-[#EEEAE4]"
                          }`}
                        >
                          <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="break-words font-bold text-[#3A3530]">
                                  {user.username}
                                </p>
                                {!submission ? (
                                  <span className="rounded-full bg-[#F7F4EF] px-2.5 py-1 text-[11px] font-bold text-[#817A72]">
                                    Не здав
                                  </span>
                                ) : (
                                  <span
                                    className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                                      submission.status === "submitted"
                                        ? "bg-[#F3EFFB] text-[#7657B5]"
                                        : submission.status === "approved"
                                        ? "bg-[#EAF7F0] text-[#25835C]"
                                        : submission.status === "revision"
                                        ? "bg-[#FFF0E8] text-[#C74712]"
                                        : "bg-[#FCE8E5] text-[#9D3D2E]"
                                    }`}
                                  >
                                    {submission.status === "submitted"
                                      ? "На перевірці"
                                      : submission.status === "approved"
                                      ? "Зараховано"
                                      : submission.status === "revision"
                                      ? "На доопрацюванні"
                                      : "Відхилено"}
                                  </span>
                                )}
                                {submission?.isLate && (
                                  <span className="rounded-full bg-[#FFF0E8] px-2.5 py-1 text-[11px] font-bold text-[#C74712]">
                                    Після дедлайну
                                  </span>
                                )}
                              </div>

                              <p className="mt-1 break-all text-xs text-[#918981]">
                                {user.email || user.telephone}
                              </p>

                              {submission ? (
                                <div className="mt-4 rounded-xl bg-white p-4 ring-1 ring-[#EEEAE4]">
                                  {submission.answerText ? (
                                    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-[#514B45]">
                                      {submission.answerText}
                                    </p>
                                  ) : (
                                    <p className="text-sm italic text-[#918981]">
                                      Текстової відповіді немає
                                    </p>
                                  )}
                                  {submission.answerUrl && (
                                    <a
                                      href={submission.answerUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="mt-3 inline-flex max-w-full items-center gap-2 break-all text-sm font-bold text-[#3867D6]"
                                    >
                                      <ExternalLink
                                        size={15}
                                        className="shrink-0"
                                      />
                                      Відкрити файл або посилання
                                    </a>
                                  )}
                                  {submission.files?.length ? (
                                    <div className="mt-4">
                                      <ProgramMaterials
                                        materials={submission.files}
                                        title="Файли відповіді"
                                      />
                                    </div>
                                  ) : null}
                                  {submission.submittedAt && (
                                    <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#918981]">
                                      <Clock3 size={13} />
                                      Надіслано{" "}
                                      {new Date(
                                        submission.submittedAt
                                      ).toLocaleString("uk-UA")}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <p className="mt-4 text-sm text-[#918981]">
                                  Учасник ще не надіслав відповідь.
                                </p>
                              )}
                            </div>

                            {submission?.status === "submitted" ? (
                              <div className="space-y-3">
                                <textarea
                                  rows={3}
                                  placeholder="Коментар учаснику"
                                  value={comments[submission._id] || ""}
                                  onChange={(event) =>
                                    setComments({
                                      ...comments,
                                      [submission._id]: event.target.value,
                                    })
                                  }
                                  className="w-full resize-none rounded-lg border border-[#DCD7CF] px-3 py-2 text-sm"
                                />
                                <label className="block text-xs font-semibold text-[#817A72]">
                                  Бали, максимум {selected.points}
                                  <input
                                    type="number"
                                    min={0}
                                    max={selected.points}
                                    value={
                                      awardedPoints[submission._id] ??
                                      selected.points
                                    }
                                    onChange={(event) =>
                                      setAwardedPoints({
                                        ...awardedPoints,
                                        [submission._id]: Number(
                                          event.target.value
                                        ),
                                      })
                                    }
                                    className="mt-1 h-10 w-full rounded-lg border border-[#DCD7CF] px-3 text-sm"
                                  />
                                </label>
                                <div className="grid gap-2 sm:grid-cols-3 xl:grid-cols-1">
                                  <button
                                    disabled={reviewingId === submission._id}
                                    onClick={() =>
                                      review(submission, "approved")
                                    }
                                    className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-[#25835C] px-3 text-xs font-bold text-white disabled:opacity-60"
                                  >
                                    <CheckCircle2 size={14} /> Зарахувати
                                  </button>
                                  <button
                                    disabled={reviewingId === submission._id}
                                    onClick={() =>
                                      review(submission, "revision")
                                    }
                                    className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-[#FFF0E8] px-3 text-xs font-bold text-[#C74712] disabled:opacity-60"
                                  >
                                    <RotateCcw size={14} /> На доопрацювання
                                  </button>
                                  <button
                                    disabled={reviewingId === submission._id}
                                    onClick={() =>
                                      review(submission, "rejected")
                                    }
                                    className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-[#FCE8E5] px-3 text-xs font-bold text-[#9D3D2E] disabled:opacity-60"
                                  >
                                    <XCircle size={14} /> Відхилити
                                  </button>
                                </div>
                              </div>
                            ) : submission ? (
                              <div className="rounded-xl bg-[#F7F4EF] p-4 text-sm text-[#625C55]">
                                <p className="font-bold">
                                  Результат перевірки
                                </p>
                                <p className="mt-2">
                                  Балів: {submission.awardedPoints}/
                                  {selected.points}
                                </p>
                                {submission.adminComment && (
                                  <p className="mt-2 whitespace-pre-wrap break-words">
                                    {submission.adminComment}
                                  </p>
                                )}
                              </div>
                            ) : null}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminAssignmentsPage;
